import { faShieldHalved } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useEffect, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'

type Cluster = {
    id: number
    score: number
    status: string
    confirmed: boolean
    reasons: string[]
    window_days: number
    first_seen_at: string
    last_seen_at: string
    review_note: string
    members: { user_id: number; username: string }[]
    pairs: any[]
}

/**
 * Why an account is flagged, opened from a withdrawal request.
 *
 * Shows the stored reasons and evidence rather than recomputing, so a reviewer
 * sees what the sweep actually found -- the thresholds and the underlying
 * sessions both move, and a decision has to be auditable against what was on
 * screen at the time.
 *
 * Unconfirmed clusters are shown too. The withdrawal *filter* is confirmed-only
 * because that gates money, but somebody deciding whether to confirm needs to
 * read the finding first.
 */
export const FraudDetail = ({ userId }: { userId: number }) => {
    const [clusters, setClusters] = useState<Cluster[]>()
    const [failed, setFailed] = useState(false)

    useEffect(() => {
        setFailed(false)
        setClusters(undefined)
        // Caught: a failed fetch used to leave "Loading fraud detail…" on screen
        // for good, which a reviewer reads as slow rather than missing (PAY-80).
        jsonrpc('bank.withdraw_request.fraud_detail', { user_id: userId })
            .then((r) => setClusters(r.result.clusters))
            .catch(() => setFailed(true))
    }, [userId])

    if (failed)
        return (
            <div className='text-[11px] text-amber-400 p-2'>
                Could not load the fraud detail for this account. Reopen the request to try again; Approve still
                refuses a confirmed cluster on the server.
            </div>
        )
    if (!clusters) return <div className='text-[11px] text-white/40 p-2'>Loading fraud detail…</div>
    // Not in one is the ordinary case, and a line saying so is another thing
    // an approver reads to learn nothing. The section appears when there is a
    // cluster to read, which is the only time it is worth their attention.
    if (!clusters.length) return null

    return (
        <div className='flex flex-col gap-3 p-2'>
            {clusters.map((cluster) => (
                <div
                    key={cluster.id}
                    className={[
                        'border p-2 flex flex-col gap-2 text-[11px]',
                        cluster.confirmed ? 'border-red-500/60 bg-red-500/5' : 'border-amber-500/40',
                    ].join(' ')}
                >
                    <div className='flex flex-row items-center gap-2 flex-wrap'>
                        <FontAwesomeIcon
                            icon={faShieldHalved}
                            className={cluster.confirmed ? 'text-red-500' : 'text-amber-500'}
                        />
                        <span className='font-bold uppercase tracking-wide'>
                            cluster #{cluster.id} · score {cluster.score}
                        </span>
                        <span className={cluster.confirmed ? 'text-red-400' : 'text-amber-400'}>
                            {cluster.status}
                        </span>
                        <span className='text-white/40'>over {cluster.window_days} days</span>
                    </div>

                    {/* The reasons are the point. A bare score invites rubber-stamping,
                        which is the failure this whole review surface exists to avoid. */}
                    <ul className='list-disc pl-4 flex flex-col gap-1 text-white/80'>
                        {cluster.reasons.map((reason) => (
                            <li key={reason}>{reason}</li>
                        ))}
                    </ul>

                    <div className='text-white/60'>
                        accounts:{' '}
                        {cluster.members.map((m) => `${m.username} (${m.user_id})`).join(', ')}
                    </div>

                    {cluster.pairs?.length > 0 && (
                        <details>
                            <summary className='cursor-pointer text-white/50 hover:text-white'>
                                per-pair evidence ({cluster.pairs.length})
                            </summary>
                            <pre className='mt-1 max-h-48 overflow-auto bg-black/40 p-2 text-[10px] leading-tight'>
                                {JSON.stringify(cluster.pairs, null, 2)}
                            </pre>
                        </details>
                    )}

                    {cluster.review_note && (
                        <div className='text-white/50'>note: {cluster.review_note}</div>
                    )}
                </div>
            ))}
        </div>
    )
}
